import React, { useState } from 'react';
import { cn } from '../../lib/cn';
import { Icon } from '../Icon/Icon';
import type { IconKind } from '../Icon/Icon';
import { CHANNEL_ICON, CHANNEL_LABEL, FIELD_BY_KEY } from '../../recall-builder/types';
import type { BuilderStep, BuilderToken, FieldKey, RecallDraft, StepChannel, StepMode } from '../../recall-builder/types';
import {
  OPT_OUT_LINE,
  channelsFor,
  fieldMenu,
  fieldsUsed,
  modesFor,
  previewPatients,
  recallLanguageLabel,
  recallOptOut,
  recallPreviewLanguage,
  recallPreviewRtl,
  resolveForPatient,
  textCountsPerLanguage,
  segmentSeverity,
  textCounts,
} from '../../recall-builder/derive';
import { MISSING_COUNTS, NO_BRIEF_YET, SMART_PREVIEW } from '../../recall-builder/mockData';
import { FieldEditor } from './FieldEditor';
import { canCallIn } from '../../platform-languages';

export interface StepEditorProps {
  draft: RecallDraft;
  step: BuilderStep;
  index: number;
  onChange: (next: BuilderStep) => void;
  onRemove: () => void;
  /** Re-sorts the list once a day has been typed (§2.5). */
  onDayCommitted?: () => void;
  /** Read from Settings › Recall, and linked there rather than restated. */
  humanCallOpenDays: number;
}

/**
 * The step editor — **board 48, mounted** (spec 21 §3.4). The old build expanded
 * steps 1, 3 and 4 to empty space; this is the panel that was designed for exactly
 * this editor and never wired in.
 */
export function StepEditor({ draft, step, index, onChange, onRemove, onDayCommitted, humanCallOpenDays }: StepEditorProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [showBackup, setShowBackup] = useState(false);
  const goal = draft.goal;
  if (goal === undefined) return null;

  const set = <K extends keyof BuilderStep>(key: K, value: BuilderStep[K]) => {
    onChange({ ...step, [key]: value });
  };

  const menu = fieldMenu({
    goal,
    mode: step.mode ?? 'template',
    hasDueCriterion: draft.readback?.hasDueCriterion ?? false,
    mentionBenefits: draft.mentionBenefits,
    channel: step.channel,
  });

  const isHuman = step.channel === 'human_call';
  const isSmart = step.mode === 'smart';
  const modes = modesFor(step.channel);

  return (
    <div className="min-w-0 flex-1 rounded-[16px] border border-[var(--border-default)] bg-[var(--surface-card)]">
      <header className="flex items-start justify-between gap-4 border-b border-[var(--border-default)] px-5 py-4">
        <div className="flex min-w-0 items-start gap-2.5">
          <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-[9px] bg-[var(--brand-indigo-tint)] text-[var(--brand-indigo)]">
            <Icon kind={CHANNEL_ICON[step.channel] as IconKind} size={14} stroke={2} />
          </span>
          <div className="min-w-0">
            <h2 className="m-0 text-[15px] font-bold text-[var(--text-primary)]">
              Step {index + 1} · {CHANNEL_LABEL[step.channel]}
            </h2>
            <p className="m-0 mt-0.5 text-[11.5px] text-[var(--text-muted)]">
              Day {step.day} after a patient starts this recall
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={onRemove}
          className="shrink-0 text-[12.5px] font-semibold text-[var(--alert-red)] hover:underline"
        >
          Remove step
        </button>
      </header>

      {/* Controls row — channel, day, and who writes it. */}
      <div className="flex flex-wrap items-end gap-5 border-b border-[var(--border-default)] px-5 py-4">
        <Labelled label="Channel">
          <select
            value={step.channel}
            aria-label="Channel"
            onChange={(e) => {
              const channel = e.target.value as StepChannel;
              const allowed = modesFor(channel);
              // A human call has no mode at all; an AI call has only Smart. Rather
              // than carrying a mode the channel cannot honour, the key is dropped.
              const { mode: _drop, ...rest } = step;
              const nextMode = allowed.includes(step.mode ?? 'template') ? step.mode : allowed[0];
              onChange(
                allowed.length === 0 || nextMode === undefined
                  ? { ...rest, channel }
                  : { ...rest, channel, mode: nextMode },
              );
            }}
            className="rounded-[9px] border border-[#dfe4ee] bg-[var(--surface-card)] px-2.5 py-1.5 text-[13px] outline-none transition-[border-color,box-shadow] hover:border-[#ccd4e4] focus:border-[var(--brand-indigo)] focus:shadow-[0_0_0_3px_rgba(76,91,212,0.14)]"
          >
            {channelsFor(goal).map((c) => (
              <option key={c} value={c}>
                {CHANNEL_LABEL[c]}
              </option>
            ))}
          </select>
        </Labelled>

        <Labelled label="Send on day">
          <input
            value={String(step.day)}
            aria-label="Send on day"
            inputMode="numeric"
            onChange={(e) => { set('day', Number(e.target.value.replace(/\D/g, '')) || 0); }}
            onBlur={onDayCommitted}
            className="w-[70px] rounded-[9px] border border-[#dfe4ee] px-2.5 py-1.5 text-[13px] outline-none transition-[border-color,box-shadow] hover:border-[#ccd4e4] focus:border-[var(--brand-indigo)] focus:shadow-[0_0_0_3px_rgba(76,91,212,0.14)]"
          />
        </Labelled>

        {modes.length > 0 ? (
          <Labelled label="Who writes it">
            <span className="inline-flex items-center gap-1 rounded-[10px] border border-[#dfe4ee] bg-[var(--surface-card)] p-[3px]" role="radiogroup" aria-label="Who writes it">
              {/* An AI call offers Smart only — a template read aloud is a robocall. */}
              {(['template', 'smart'] as StepMode[]).map((m) => {
                const allowed = modes.includes(m);
                if (!allowed) return null;
                const on = (step.mode ?? 'template') === m;
                return (
                  <label
                    key={m}
                    className={cn(
                      'cursor-pointer rounded-[7px] px-3 py-1 text-[12px] font-semibold',
                      on ? 'bg-[var(--brand-indigo-tint)] text-[var(--brand-indigo)]' : 'text-[var(--text-secondary)]',
                    )}
                  >
                    <input
                      type="radio"
                      name={`mode-${step.id}`}
                      checked={on}
                      onChange={() => {
                        // Switching to Smart used to THROW THE MESSAGE AWAY. The
                        // words somebody wrote are exactly what the backup needs
                        // to be, so they move there rather than vanishing.
                        if (m === 'smart' && (step.backup ?? []).length === 0 && (step.body ?? []).length > 0) {
                          onChange({ ...step, mode: m, backup: step.body ?? [], body: [] });
                        } else if (m === 'template' && (step.body ?? []).length === 0 && (step.backup ?? []).length > 0) {
                          // And back again, so the switch is not a one-way door.
                          onChange({ ...step, mode: m, body: step.backup ?? [] });
                        } else {
                          set('mode', m);
                        }
                      }}
                      className="sr-only"
                    />
                    {m === 'template' ? 'Template' : 'Smart'}
                  </label>
                );
              })}
            </span>
          </Labelled>
        ) : null}

        {modes.length > 0 ? (
          <p className="m-0 min-w-[240px] flex-1 text-[11.5px] leading-[1.5] text-[var(--text-muted)]">
            {isSmart
              ? `ELVA writes each ${step.channel === 'email' ? 'email' : 'message'} from your brief. It checks every detail against the patient’s record before sending.`
              : 'You write the words. Fields fill in each patient’s details.'}
          </p>
        ) : null}
      </div>

      <div className="px-5 py-4">
        {isHuman ? (
          <>
            <p className="m-0 text-[13px] leading-[1.7] text-[var(--text-secondary)]">
              Adds a call to Triage Inbox. Your team has{' '}
              <a href="/settings/ai/recall" className="font-semibold text-[var(--brand-indigo)] hover:underline">
                {humanCallOpenDays} open days
              </a>{' '}
              to make it.
            </p>
            <Section label="Note for the caller" sub="Optional. Anything the person ringing should know before they do.">
              <textarea
                rows={3}
                value={step.callerNote ?? ''}
                aria-label="Note for the caller"
                onChange={(e) => { set('callerNote', e.target.value); }}
                className="w-full resize-y rounded-[10px] border border-[#dfe4ee] px-3.5 py-2.5 text-[13.5px] leading-[1.6] outline-none transition-[border-color,box-shadow] hover:border-[#ccd4e4] focus:border-[var(--brand-indigo)] focus:shadow-[0_0_0_3px_rgba(76,91,212,0.14)]"
              />
            </Section>
            <Section label="How it reads" sub="The task exactly as staff will see it in Triage Inbox.">
              <div className="rounded-[12px] border border-[var(--border-default)] bg-[#FAFBFE] p-3.5">
                <div className="text-[12.5px] font-bold text-[var(--text-primary)]">Call a recall patient</div>
                <div className="mt-1 text-[11.5px] leading-[1.6] text-[var(--text-secondary)]">
                  {draft.name === '' ? 'Untitled recall' : draft.name} · due in {humanCallOpenDays} open days
                  {(step.callerNote ?? '').trim() === '' ? null : <> · {step.callerNote}</>}
                </div>
                <div className="mt-1.5 text-[11px] text-[var(--text-muted)]">
                  Carries the patient, why they are being called, and what the earlier steps already said.
                </div>
              </div>
            </Section>
          </>
        ) : (
          <>
            {step.channel === 'email' ? (
              <Section label="Subject line">
                <FieldEditor
                  ariaLabel="Subject line"
                  rows={1}
                  menu={menu}
                  value={step.subject ?? []}
                  onChange={(v) => { set('subject', v); }}
                  onMenuOpenChange={setMenuOpen}
                  placeholder="What they see in their inbox"
                />
              </Section>
            ) : null}

            {isSmart ? (
              <>
                <Section
                  label="What should it say?"
                  sub={`Tell ELVA what the ${step.channel === 'email' ? 'email' : 'message'} is for and how it should sound. You don’t write the ${step.channel === 'email' ? 'email' : 'message'} itself.`}
                >
                  <textarea
                    rows={4}
                    value={step.brief ?? ''}
                    aria-label="What should it say?"
                    onChange={(e) => { set('brief', e.target.value); }}
                    className="w-full resize-y rounded-[10px] border border-[#dfe4ee] px-3.5 py-2.5 text-[13.5px] leading-[1.7] outline-none transition-[border-color,box-shadow] hover:border-[#ccd4e4] focus:border-[var(--brand-indigo)] focus:shadow-[0_0_0_3px_rgba(76,91,212,0.14)]"
                  />
                </Section>

                <Section label="It must include" sub="If ELVA’s message leaves any of these out, the backup below is sent instead.">
                  <MustInclude
                    selected={step.mustInclude ?? []}
                    menu={menu}
                    onChange={(v) => { set('mustInclude', v); }}
                  />
                </Section>

                <Section label="Backup message" sub="Required. Sent if ELVA can’t write the message for any reason, so a patient is never skipped.">
                  <FieldEditor
                    ariaLabel="Backup message"
                    menu={menu}
                    value={step.backup ?? []}
                    onChange={(v) => { set('backup', v); }}
                    onMenuOpenChange={setMenuOpen}
                    placeholder="The words that go out if ELVA can’t write it"
                  />
                </Section>
              </>
            ) : (
              <Section label="Message">
                <FieldEditor
                  ariaLabel="Message"
                  menu={menu}
                  value={step.body ?? []}
                  onChange={(v) => { set('body', v); }}
                  onMenuOpenChange={setMenuOpen}
                  placeholder="What every patient gets, with their own details filled in"
                />
              </Section>
            )}

            {step.channel === 'text' ? <TextCounter step={step} /> : null}

            {step.channel === 'ai_call' ? (
              <Section label="If nobody answers" sub="A voicemail counts as contact, the same as a text would.">
                <div role="radiogroup" aria-label="If nobody answers" className="flex flex-col gap-1">
                  {(
                    [
                      ['leave_and_stop', 'Leave a voicemail and stop', 'One message, and ELVA carries on to the next step.'],
                      ['leave_and_retry', 'Leave a voicemail and try once more', 'A second attempt the same day, at least four hours later.'],
                      ['no_voicemail', 'Do not leave a voicemail', 'Ring, and if nobody picks up, nothing was said.'],
                    ] as const
                  ).map(([v, label, desc]) => {
                    const on = (step.voicemail ?? 'leave_and_stop') === v;
                    return (
                      <label
                        key={v}
                        className={cn(
                          'flex cursor-pointer items-start gap-2.5 rounded-[9px] px-3 py-2',
                          on ? 'bg-[var(--brand-indigo-tint)]' : 'hover:bg-[var(--surface-sunken)]',
                        )}
                      >
                        <input
                          type="radio"
                          name={`vm-${step.id}`}
                          checked={on}
                          onChange={() => { set('voicemail', v); }}
                          className="mt-1 h-[14px] w-[14px] accent-[var(--brand-indigo)]"
                        />
                        <span>
                          <span className={cn('block text-[13px]', on ? 'font-bold text-[var(--text-primary)]' : 'text-[var(--text-secondary)]')}>
                            {label}
                          </span>
                          <span className="block text-[11.5px] text-[var(--text-muted)]">{desc}</span>
                        </span>
                      </label>
                    );
                  })}
                </div>
              </Section>
            ) : null}

            <MissingDetails step={step} onChange={onChange} />

            <Preview
              draft={draft}
              step={step}
              menuOpen={menuOpen}
              showBackup={showBackup}
              onToggleBackup={() => { setShowBackup(!showBackup); }}
            />
          </>
        )}
      </div>
    </div>
  );
}

/**
 * *It must include* — the things ELVA may not leave out, as removable chips.
 * These are fields rather than free text, because a phrase ELVA has to match
 * loosely is a rule nobody can check; a field either appeared or it did not.
 */
function MustInclude({
  selected,
  menu,
  onChange,
}: {
  selected: FieldKey[];
  menu: { field: { key: FieldKey; label: string }; unavailable: string | undefined }[];
  onChange: (next: FieldKey[]) => void;
}) {
  const [adding, setAdding] = useState(false);
  const available = menu.filter((m) => m.unavailable === undefined && !selected.includes(m.field.key));
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {selected.map((key) => (
        <span
          key={key}
          className="inline-flex items-center gap-1.5 rounded-full bg-[var(--brand-indigo-tint)] px-2.5 py-1 text-[11.5px] font-semibold text-[var(--brand-indigo)]"
        >
          {FIELD_BY_KEY[key].label}
          <button
            type="button"
            aria-label={`Remove ${FIELD_BY_KEY[key].label}`}
            onClick={() => { onChange(selected.filter((k) => k !== key)); }}
            className="text-[13px] leading-none hover:text-[var(--alert-red)]"
          >
            ×
          </button>
        </span>
      ))}
      {adding ? (
        <span className="inline-flex flex-wrap items-center gap-1.5">
          {available.length === 0 ? (
            <span className="text-[11.5px] text-[var(--text-muted)]">Every available field is already on the list.</span>
          ) : (
            available.map((m) => (
              <button
                key={m.field.key}
                type="button"
                onClick={() => {
                  onChange([...selected, m.field.key]);
                  setAdding(false);
                }}
                className="rounded-full border border-dashed border-[var(--brand-indigo)] px-2.5 py-1 text-[11.5px] font-semibold text-[var(--brand-indigo)]"
              >
                {m.field.label}
              </button>
            ))
          )}
          <button
            type="button"
            aria-label="Close"
            onClick={() => { setAdding(false); }}
            className="text-[var(--text-muted)]"
          >
            <Icon kind="x" size={12} stroke={2.2} />
          </button>
        </span>
      ) : (
        <button
          type="button"
          onClick={() => { setAdding(true); }}
          className="rounded-full border border-[var(--border-default)] px-2.5 py-1 text-[11.5px] font-semibold text-[var(--brand-indigo)] hover:bg-[var(--brand-indigo-tint)]"
        >
          + Add
        </button>
      )}
    </div>
  );
}

function Labelled({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <span className="inline-block">
      <span className="block text-[10.5px] font-bold uppercase tracking-[0.09em] text-[var(--text-muted)]">{label}</span>
      <span className="mt-1 block">{children}</span>
    </span>
  );
}

function Section({
  label,
  sub,
  right,
  children,
}: {
  label: string;
  sub?: string;
  /** A control that belongs to the section rather than to a row inside it. */
  right?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="mb-5 last:mb-0">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-[10.5px] font-bold uppercase tracking-[0.09em] text-[var(--text-muted)]">{label}</div>
        {right}
      </div>
      {sub !== undefined ? <p className="m-0 mb-2 mt-1 text-[11.5px] leading-[1.55] text-[var(--text-muted)]">{sub}</p> : null}
      <div className={sub === undefined ? 'mt-2' : ''}>{children}</div>
    </div>
  );
}

/** The counter includes the opt-out line, because that line is going out too. */
function TextCounter({ step }: { step: BuilderStep }) {
  const tokens = step.mode === 'smart' ? (step.backup ?? []) : (step.body ?? []);
  const { chars, segments } = textCounts(tokens);
  const severity = segmentSeverity(segments);
  return (
    <div className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-[10px] bg-[var(--surface-sunken)] px-3.5 py-2.5">
      <span
        className="text-[11.5px]"
        style={{
          color:
            severity === 'block' ? 'var(--alert-red)' : severity === 'warn' ? 'var(--warn-amber)' : 'var(--text-muted)',
        }}
      >
        <b className="font-[var(--font-mono)] font-bold">{chars}</b> characters ·{' '}
        <b className="font-[var(--font-mono)] font-bold">{segments}</b> text message{segments === 1 ? '' : 's'}
        {severity === 'warn' ? ' — longer than two is a lot to read on a phone' : ''}
        {severity === 'block' ? ' — too long to send' : ''}
      </span>
      {/* One count per language (amendment 25 §7). A single segment count is
          wrong for one of them: Farsi fits 70 characters a part, not 160, so a
          message that is one text in English is three in Farsi. */}
      <span className="flex flex-wrap items-baseline gap-x-3 text-[11.5px] text-[var(--text-muted)]">
        {textCountsPerLanguage(tokens).map((c) => (
          <span key={c.code}>
            {c.name} <b className="font-[var(--font-mono)] font-bold">{c.texts}</b>{' '}
            {c.texts === 1 ? 'text' : 'texts'}
          </span>
        ))}
        <span>“{OPT_OUT_LINE}” is added to the end for you.</span>
      </span>
    </div>
  );
}

/**
 * *If a detail is missing* — one row per field **some patients lack**. A field
 * everybody has says so and needs nothing; one with an empty fallback is an issue.
 */
function MissingDetails({ step, onChange }: { step: BuilderStep; onChange: (next: BuilderStep) => void }) {
  const used = fieldsUsed(step).filter((k) => FIELD_BY_KEY[k].mayBeEmpty);
  const known = fieldsUsed(step).filter((k) => !FIELD_BY_KEY[k].mayBeEmpty);
  if (used.length === 0 && known.length === 0) return null;
  return (
    <Section label="If a detail is missing" sub="Only details in this message that some patients don’t have are listed.">
      <div className="flex flex-col gap-2">
        {used.map((key) => {
          const field = FIELD_BY_KEY[key];
          const value = step.fallbacks?.[key] ?? field.defaultFallback ?? '';
          const missing = MISSING_COUNTS[key];
          return (
            <div key={key} className="flex flex-wrap items-center justify-between gap-2">
              <span className="flex items-center gap-2 text-[12.5px] text-[var(--text-secondary)]">
                <span className="rounded-[6px] bg-[var(--brand-indigo-tint)] px-1.5 py-0.5 text-[12px] font-semibold text-[var(--brand-indigo)]">
                  {field.label}
                </span>
                if it’s missing, say
                <input
                  value={value}
                  aria-label={`Fallback for ${field.label}`}
                  onChange={(e) => {
                    onChange({ ...step, fallbacks: { ...step.fallbacks, [key]: e.target.value } });
                  }}
                  className={cn(
                    'w-[140px] rounded-[8px] border px-2 py-1 text-[12.5px] outline-none',
                    value.trim() === ''
                      ? 'border-[var(--alert-red)]'
                      : 'border-[var(--border-default)] focus:border-[var(--brand-indigo)]',
                  )}
                />
              </span>
              {missing !== undefined ? (
                <span className="text-[11px] text-[var(--text-muted)]">
                  {missing} patients have no {field.label.toLowerCase()} on file
                </span>
              ) : null}
            </div>
          );
        })}
        {known.map((key) => (
          <div key={key} className="flex flex-wrap items-center gap-2 text-[12px] text-[var(--text-muted)]">
            <span className="rounded-[6px] bg-[#F1F3F9] px-1.5 py-0.5 text-[11.5px] font-semibold text-[var(--text-secondary)]">
              {FIELD_BY_KEY[key].label}
            </span>
            known for every patient in this recall — no fallback needed
          </div>
        ))}
      </div>
    </Section>
  );
}

/** Three real patients, updating as you type. */
function Preview({
  draft,
  step,
  menuOpen,
  showBackup,
  onToggleBackup,
}: {
  draft: RecallDraft;
  step: BuilderStep;
  menuOpen: boolean;
  showBackup: boolean;
  onToggleBackup: () => void;
}) {
  // The same three the test page previews — and never one who was left out (§3.4).
  const patients = previewPatients(draft);
  const smart = step.mode === 'smart' && !showBackup;
  // English, or each patient's own (amendment 25 §7). Without the switch a
  // practice writing English cannot tell whether the Farsi version exists at all;
  // with only the patient's own, they cannot proofread their own words.
  const [showEnglish, setShowEnglish] = useState(false);
  return (
    <Section
      label="How it reads"
      sub={`Three real patients from this recall, each in their language.${step.mode === 'smart' ? '' : ' Updates as you type.'}`}
      right={
        <span className="inline-flex gap-1 rounded-[9px] bg-[var(--surface-sunken)] p-[3px]">
          {[
            { on: true, label: 'English' },
            { on: false, label: 'Each patient’s own' },
          ].map((o) => (
            <button
              key={o.label}
              type="button"
              onClick={() => {
                setShowEnglish(o.on);
              }}
              className={cn(
                'rounded-[7px] px-2.5 py-1 text-[12px]',
                o.on === showEnglish
                  ? 'bg-[var(--surface-card)] font-semibold text-[var(--text-primary)] shadow-[0_1px_2px_rgba(36,41,54,.08)]'
                  : 'text-[var(--text-secondary)]',
              )}
            >
              {o.label}
            </button>
          ))}
        </span>
      }
    >
      {step.mode === 'smart' ? (
        <div className="mb-2 flex items-center gap-3">
          <button type="button" onClick={onToggleBackup} className="text-[11.5px] font-semibold text-[var(--brand-indigo)] hover:underline">
            {showBackup ? 'Show what ELVA writes' : 'Show the backup'}
          </button>
          <button type="button" className="text-[11.5px] font-semibold text-[var(--brand-indigo)] hover:underline">
            Write again
          </button>
        </div>
      ) : null}
      <div className="grid grid-cols-3 gap-2.5 max-[1100px]:grid-cols-1">
        {patients.map((p) => {
          const tokens: BuilderToken[] = smart ? [] : step.mode === 'smart' ? (step.backup ?? []) : (step.body ?? []);
          const resolved = resolveForPatient(tokens, p, step.fallbacks);
          const generated = SMART_PREVIEW[p.id];
          const subject = step.channel === 'email' ? resolveForPatient(step.subject ?? [], p, step.fallbacks).text : '';
          // A call is SPOKEN. The old preview showed the text-message copy —
          // "One tap and it's done" — for a phone call (§3.1).
          const isCall = step.channel === 'ai_call';
          const briefEmpty = (step.brief ?? '').trim() === '';
          const language = recallPreviewLanguage(p, showEnglish);
          const rtl = recallPreviewRtl(p, showEnglish);
          const english = smart
            ? isCall
              ? (briefEmpty ? '' : (generated?.spoken ?? ''))
              : (generated?.body ?? '')
            : resolved.text;
          // Her own language when there is one and the switch asks for it — the
          // practice writes English and ELVA sends each patient theirs (§5).
          const words = !showEnglish && p.rendered !== undefined && !isCall ? p.rendered : english;
          return (
            <div key={p.id} className="rounded-[10px] border border-[var(--border-default)] bg-[#FAFBFE] p-3">
              <div className="flex flex-wrap items-baseline gap-x-2">
                <span className="text-[11.5px] font-semibold text-[var(--text-primary)]">{p.name}</span>
                <span className="text-[10.5px] text-[var(--text-muted)]">{recallLanguageLabel(p)}</span>
              </div>
              <div className="text-[10.5px] text-[var(--text-muted)]">due {p.due}</div>
              {step.channel === 'email' ? (
                <div className="mt-1.5 text-[11.5px] font-semibold text-[var(--text-secondary)]">
                  {smart ? (generated?.subject ?? '—') : subject}
                </div>
              ) : null}
              {isCall && briefEmpty && smart ? (
                <div className="mt-1 text-[11.5px] italic leading-[1.6] text-[var(--text-muted)]">{NO_BRIEF_YET}</div>
              ) : (
                <div
                  dir={rtl ? 'rtl' : undefined}
                  className="mt-1 text-[11.5px] leading-[1.6] text-[var(--text-secondary)]"
                >
                  {isCall ? <span className="text-[var(--text-muted)]">“</span> : null}
                  {words}
                  {isCall ? <span className="text-[var(--text-muted)]">”</span> : null}
                  {/* While the field menu is open the preview says where the chip
                      will land, rather than showing a half-typed brace. */}
                  {menuOpen ? (
                    <span className="ml-1 rounded-[5px] bg-[var(--brand-indigo-tint)] px-1.5 py-0.5 text-[10.5px] font-semibold text-[var(--brand-indigo)]">
                      choosing a field…
                    </span>
                  ) : null}
                </div>
              )}
              {/* A real button, not the literal words `[Pick a time]` (§3.2).
                  A call gets none — there is nothing to tap in a phone call. */}
              {!isCall && step.channel === 'email' && smart ? (
                <span className="mt-2 inline-block rounded-[6px] bg-[var(--brand-indigo)] px-2 py-1 text-[10.5px] font-semibold text-white">
                  Pick a time
                </span>
              ) : null}
              {/* The opt-out line goes in HER language — our own reviewed
                  translation, not the model's (§5). */}
              {step.channel === 'text' ? (
                <div dir={rtl ? 'rtl' : undefined} className="mt-1.5 text-[10px] text-[var(--text-muted)]">
                  {recallOptOut(p, showEnglish)}
                </div>
              ) : null}
              {/* A call in a language ELVA cannot speak yet is skipped and the
                  text sent — never a silent nothing (§5). */}
              {isCall && !canCallIn(language) ? (
                <div className="mt-1.5 text-[10.5px] text-[var(--warn-amber)]">
                  ELVA can’t call in {recallLanguageLabel(p)} yet — the text version goes instead.
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </Section>
  );
}
